import React, { useEffect, useRef, FC, useMemo } from "react";
import { Route, Routes, useNavigate, useParams, useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { useTrackCreation } from "../../../context/TrackCreationContext";
import { useGlobalState } from "../../../context/useGlobalState";
import shell from "./createShell.module.css";
import AccessSecurityScreen from "./Basic/AccessSecurity";
import CategoryScreen from "./Basic/Category";
import DescriptionScreen from "./Basic/Description";
import ExtraScreen from "./Basic/Extra";
import LocationScreen from "./Basic/Location";
import PhotosScreen from "./Basic/Photos";
import TitleScreen from "./Basic/Title";
import Itinerary from "./Map/Itinerary";
import MapContent from "./Map/MapContent";
import { QuizContent } from "./Quiz/QuizOnMap";
import QuizLanguage from "./Quiz/SubScreens/Language";
import QuizQuestion from "./Quiz/SubScreens/Title";
import QuizCoordinates from "./Quiz/SubScreens/Coordinates";
import QuizPicture from "./Quiz/SubScreens/Picture";
import IncompleteData from "./Map/IncompleteData";
import MuseumPlansAndZones from "./Museum/Architecture";
import MuseumContent from "./Museum/Stories";
import QRCodes from "./Publication/QRCodes";
import StopsHOHO from "./HopOnHopOff/Stops";
import BusTracking from "./HopOnHopOff/Tracking";
import Music from "./HopOnHopOff/Music";
import StopLanguage from "./HopOnHopOff/StopsSubscreens/Language";
import StopInfo from "./HopOnHopOff/StopsSubscreens/Information";
import StopActivities from "./HopOnHopOff/StopsSubscreens/Activities";
import Partners from "./HotInfo/Partners";
import MuseumInfo from "./Basic/Museum/Information";
import WaypointLanguage from "./Waypoint/Language";
import WaypointTitle from "./Waypoint/Title";
import WaypointCoordinates from "./Waypoint/Coordinates";
import WaypointText from "./Waypoint/Text";
import WaypointAudio from "./Waypoint/Audio";
import WaypointInfo from "./Waypoint/Settings";
import WaypointPicture from "./Waypoint/Pictures";
import HotInfoLanguage from "./HotInfo/Subscreens/Language";
import HotInfoCoordinates from "./HotInfo/Subscreens/Coordinates";
import HotInfoContent from "./HotInfo/Subscreens/Content";
import HotInfoPictures from "./HotInfo/Subscreens/Pictures";
import RoomLanguage from "./Museum/Subscreens/Rooms/Language";
import RoomTitle from "./Museum/Subscreens/Rooms/Title";
import RoomPlan from "./Museum/Subscreens/Rooms/Plan";
import ZoneLanguage from "./Museum/Subscreens/Zones/Language";
import ZoneInfo from "./Museum/Subscreens/Zones/Information";
import StoryLanguage from "./Museum/Subscreens/Stories/Language";
import StoryTitle from "./Museum/Subscreens/Stories/Title";
import StoryZone from "./Museum/Subscreens/Stories/Zone";
import StoryText from "./Museum/Subscreens/Stories/Text";
import StoryAudio from "./Museum/Subscreens/Stories/Audio";
import StoryPhotos from "./Museum/Subscreens/Stories/Photo";
import Sidebar from "../../../components/sidebars/TrackSidebar";
import QuizSidebar from "../../../components/sidebars/QuizSidebar";
import StopsSidebar from "../../../components/sidebars/StopsSidebar";
import HotInfoSidebar from "../../../components/sidebars/HotInfoSidebar";
import WaypointSidebar from "../../../components/sidebars/WaypointSidebar";
import MuseumZonesSidebar from "../../../components/sidebars/MuseumZonesSidebar";
import MuseumRoomsSidebar from "../../../components/sidebars/MuseumRoomsSidebar";
import MuseumStoriesSidebar from "../../../components/sidebars/MuseumStoriesSidebar";
import TranslateTrack from "./Publication/LanguageActivation";
// ScanConfig pages for image scan tracks
import { MainTheme, ManageObjects, CreateObject, ModifyObject } from "./ScanConfig";
// Stories pages for image scan tracks
import { ManageStories, CreateStory, ModifyStory } from "./Stories";
// Content Playlist pages
import { ManageContentPlaylist, CreateContentPlaylistItem, ModifyContentPlaylistItem } from "./ContentPlaylist";
// Progression pages for gamification
import {
    DefaultAvatar,
    ManageLevels,
    CreateLevel,
    ModifyLevel,
    ManageBadges,
    CreateBadge,
    ModifyBadge,
    ManageRewards,
    CreateReward,
    ModifyReward,
    SocialShare,
} from "./Progression";

const TrackCreationFlow: FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { trackId } = useParams<{ trackId?: string }>(); // trackId is optional

    const { trackData, loadTrackData, createTrack, saveTrack } = useTrackCreation();
    const { data: globalState } = useGlobalState();
    const generatedId = useRef<string>(trackId || uuidv4());

    // Check if this is an image scan track (Scan IA filter selected)
    const isImageScanTrack = useMemo(() => {
        if (!trackData?.subType || !globalState?.filtersAI) return false;
        const filter = globalState.filtersAI.find((f) => f.value === trackData.subType);
        return filter?.hasOnlyImageScanWaypoints || false;
    }, [trackData?.subType, globalState?.filtersAI]);

    // Load or create a new track only once when the component mounts
    useEffect(() => {
        if (!trackId) {
            // If there's no trackId in the URL, navigate to a new ID
            navigate(`/create/tour/${generatedId.current}/category`, {
                replace: true,
            });
            createTrack(generatedId.current);
        } else {
            // If an ID exists in the URL, load the track data if needed
            if (trackId !== trackData.id && trackId !== trackData.trackId) {
                loadTrackData(trackId);
            }
        }
    }, [trackId, createTrack, loadTrackData, navigate, trackData.id, trackData.trackId]);

    const scrollCreateContentToTop = () => {
        // Create shell (`.track-creation-flow-content`) owns overflow; sometimes `.app-content` too.
        // Nested left columns (mapShell) can keep their own offset after Continuer.
        const reset = () => {
            const scroll = (el: Element | null) => {
                if (el instanceof HTMLElement) {
                    el.scrollTo({ top: 0, left: 0, behavior: "auto" });
                    el.scrollTop = 0;
                }
            };
            scroll(document.querySelector(".track-creation-flow-content"));
            scroll(document.querySelector(".app-content"));
            document
                .querySelectorAll(
                    ".track-creation-flow-content [class*='left'], .track-creation-flow-content [class*='Left']",
                )
                .forEach((el) => {
                    if (el instanceof HTMLElement && el.scrollHeight > el.clientHeight + 1) {
                        scroll(el);
                    }
                });
            window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        };
        requestAnimationFrame(() => {
            reset();
            // Second pass after layout (map shells / lazy panels).
            requestAnimationFrame(reset);
        });
    };

    // Sidebar Links and local navigate() bypass goToStep* — always reset on route change.
    useEffect(() => {
        scrollCreateContentToTop();
    }, [location.pathname]);

    const goToStep = async (path: string, saveTrackToDb?: boolean) => {
        let id: String | null | undefined = trackData.trackId;
        if (saveTrackToDb) {
            id = await saveTrack();
        }
        if (!id && (path === "itinerary" || path === "museum-architecture")) {
            return;
        }
        navigate(`/create/tour/${id ? id : trackData.id}/${path}`);
        scrollCreateContentToTop();
    };

    const goToStepMuseumInfo = async (path: string, saveTrackToDb?: boolean) => {
        let id: String | null | undefined = trackData.trackId;
        if (saveTrackToDb) {
            id = await saveTrack();
        }
        const pathRedirect = trackData.type === "iduniquemusee" ? "museum-info" : path;
        navigate(`/create/tour/${id ? id : trackData.id}/${pathRedirect}`);
        scrollCreateContentToTop();
    };

    const goToWaypointScreen = (path: string, id: string, lang?: string) => {
        if (trackData.trackId) {
            navigate(`/create/tour/${trackData.trackId}/${path}/${id}${lang ? `/${lang}` : ""}`);
            scrollCreateContentToTop();
        }
    };

    const goToMapContentScreen = (path: string, id?: string | null, lang?: string) => {
        if (trackData.trackId) {
            const idSegment = id ? `/${id}` : "";
            const langSegment = lang ? `/${lang}` : "";
            navigate(`/create/tour/${trackData.trackId}/${path}${idSegment}${langSegment}`);
            scrollCreateContentToTop();
        }
    };

    const goToMuseumStoriesScreen = (path: string, subplanId: string, id: string, lang?: string) => {
        if (trackData.trackId) {
            navigate(`/create/tour/${trackData.trackId}/${path}/${subplanId}/${id}${lang ? `/${lang}` : ""}`);
            scrollCreateContentToTop();
        }
    };

    const goToHotInfoScreen = (path: string, id: string, lang?: string) => {
        if (trackData.trackId) {
            navigate(`/create/tour/${trackData.trackId}/partners/${path}/${id}${lang ? `/${lang}` : ""}`);
            scrollCreateContentToTop();
        }
    };

    const isWaypointSection = location.pathname.includes("/waypoint-");
    const isPartnerSection = location.pathname.includes("/partners-");
    const isMuseumSection = location.pathname.includes("/room-");
    const isZoneMuseumSection = location.pathname.includes("/zone-");
    const isStoryMuseumSection = location.pathname.includes("/story-");
    const isStopsSection = location.pathname.includes("/stops-");
    const isQuizSection = location.pathname.includes("/quiz-") && !location.pathname.includes("/quiz-content");

    return (
        <div className={shell.root}>
            <div className={`track-creation-flow ${shell.flow}`}>
                <div className={shell.sidebar}>
                    {isStoryMuseumSection ? (
                        <MuseumStoriesSidebar />
                    ) : isZoneMuseumSection ? (
                        <MuseumZonesSidebar />
                    ) : isMuseumSection ? (
                        <MuseumRoomsSidebar />
                    ) : isWaypointSection ? (
                        <WaypointSidebar />
                    ) : isPartnerSection ? (
                        <HotInfoSidebar />
                    ) : isStopsSection ? (
                        <StopsSidebar />
                    ) : isQuizSection ? (
                        <QuizSidebar />
                    ) : (
                        <Sidebar trackData={trackData} />
                    )}
                </div>
                <div className={`track-creation-flow-content ${shell.content}`}>
                    <Routes>
                        <Route
                            path="category"
                            element={<CategoryScreen navigateToNext={() => goToStep("title", true)} />}
                        />
                        <Route
                            path="title"
                            element={<TitleScreen navigateToNext={() => goToStepMuseumInfo("description", true)} />}
                        />
                        <Route
                            path="description"
                            element={<DescriptionScreen navigateToNext={() => goToStep("location", true)} />}
                        />
                        <Route
                            path="location"
                            element={<LocationScreen navigateToNext={() => goToStep("extra", true)} />}
                        />
                        <Route path="extra" element={<ExtraScreen navigateToNext={() => goToStep("prices", true)} />} />
                        <Route
                            path="prices"
                            element={<AccessSecurityScreen navigateToNext={() => goToStep("photos", true)} />}
                        />
                        <Route
                            path="photos"
                            element={
                                <PhotosScreen
                                    navigateToNext={async () => {
                                        if (trackData.type === "iduniquemusee") {
                                            // Museum track
                                            await goToStep("museum-architecture", true);
                                        } else if (isImageScanTrack) {
                                            // Image scan track (Scan IA) - go to main-theme
                                            await goToStep("main-theme", true);
                                        } else if (
                                            (trackData.waypoints && trackData.waypoints.length > 0) ||
                                            trackData.hasExcelGenerated === true
                                        ) {
                                            // Regular track with existing waypoints
                                            await goToStep("map-content", true);
                                        } else {
                                            // Regular track without waypoints
                                            await goToStep("itinerary", true);
                                        }
                                    }}
                                />
                            }
                        />
                        <Route
                            path="itinerary"
                            element={<Itinerary navigateToNext={() => goToStep("map-content", true)} />}
                        />
                        <Route
                            path="map-content"
                            element={
                                <MapContent
                                    navigateToNext={(waypointId: string) =>
                                        goToWaypointScreen("waypoint-language", waypointId)
                                    }
                                    navigateToContinue={() => {
                                        // Same order as TrackSidebar publication steps after map
                                        const isMuseum = trackData.type === "iduniquemusee";
                                        const isHoho = trackData.type === "iduniquebus";
                                        if (isHoho && trackData?.subType === "cl2j5yyhr0014k4qxtbgb4cpx") {
                                            goToStep("stop-content", true);
                                        } else if (!isMuseum) {
                                            goToStep("quiz-content", true);
                                        } else if (isHoho) {
                                            goToStep("bus-music", true);
                                        } else {
                                            goToStep("partners", true);
                                        }
                                    }}
                                />
                            }
                        />
                        <Route path="translate" element={<TranslateTrack />} />
                        <Route
                            path="quiz-content"
                            element={
                                <QuizContent
                                    navigateToNext={(id: string) => goToWaypointScreen("quiz-language", id)}
                                    navigateToContinue={() => {
                                        const isHoho = trackData.type === "iduniquebus";
                                        if (isHoho) {
                                            goToStep("bus-music", true);
                                        } else {
                                            goToStep("content-playlist", true);
                                        }
                                    }}
                                />
                            }
                        />
                        <Route
                            path="quiz-language/:quizId/*"
                            element={
                                <QuizLanguage
                                    navigateToNext={(quizId: string, selectedLanguage: string) =>
                                        goToWaypointScreen("quiz-title", quizId, selectedLanguage)
                                    }
                                />
                            }
                        />
                        <Route
                            path="quiz-title/:quizId/:language"
                            element={
                                <QuizQuestion
                                    navigateToNext={(id: string, lang: string) =>
                                        goToWaypointScreen("quiz-coordinates", id, lang)
                                    }
                                />
                            }
                        />
                        <Route
                            path="quiz-coordinates/:quizId/:language"
                            element={
                                <QuizCoordinates
                                    navigateToNext={(id: string, lang: string) =>
                                        goToWaypointScreen("quiz-picture", id, lang)
                                    }
                                />
                            }
                        />
                        <Route path="quiz-picture/:quizId/:language" element={<QuizPicture />} />
                        <Route
                            path="incomplete-data"
                            element={<IncompleteData navigateToContinue={() => goToStep("translate", true)} />}
                        />
                        <Route
                            path="museum-architecture"
                            element={
                                <MuseumPlansAndZones
                                    navigateToNext={(id: string | null) => goToMapContentScreen("room-language", id)}
                                    navigateToPrevious={(id: string | null) =>
                                        goToMapContentScreen("zone-language", id)
                                    }
                                    navigateToContinue={() => goToStep("museum-stories", true)}
                                />
                            }
                        />
                        <Route
                            path="museum-stories"
                            element={
                                <MuseumContent
                                    navigateToNext={(id: string, subPlansId: string) =>
                                        goToMuseumStoriesScreen("story-language", subPlansId, id)
                                    }
                                    navigateToContinue={() => goToStep("incomplete-data", true)}
                                />
                            }
                        />
                        <Route path="qrcode" element={<QRCodes />} />
                        <Route
                            path="stop-content"
                            element={
                                <StopsHOHO
                                    navigateToNext={(id: string) => goToWaypointScreen("stops-language", id)}
                                    navigateToContinue={() => goToStep("bus-tracking", true)}
                                />
                            }
                        />
                        <Route
                            path="bus-tracking"
                            element={<BusTracking navigateToNext={() => goToStep("quiz-content")} />}
                        />
                        <Route path="bus-music" element={<Music />} />
                        <Route
                            path="stops-language/:wptId/*"
                            element={
                                <StopLanguage
                                    navigateToNext={(id: string, lang: string) =>
                                        goToWaypointScreen("stops-info", id, lang)
                                    }
                                />
                            }
                        />
                        <Route
                            path="stops-info/:wptId/:language"
                            element={
                                <StopInfo
                                    navigateToNext={(id: string, lang: string) =>
                                        goToWaypointScreen("stops-activities", id, lang)
                                    }
                                />
                            }
                        />
                        <Route
                            path="stops-activities/:wptId/:language"
                            element={
                                <StopActivities
                                    navigateToNext={(id: string, lang: string) => goToStep("stop-content", true)}
                                />
                            }
                        />
                        <Route
                            path="partners"
                            element={
                                <Partners
                                    navigateToNext={(id: string) => goToHotInfoScreen("partners-language", id)}
                                    navigateToContinue={() => goToStep("incomplete-data", true)}
                                />
                            }
                        />
                        <Route
                            path="museum-info"
                            element={<MuseumInfo navigateToNext={() => goToStep("description", true)} />}
                        />
                        <Route
                            path="waypoint-language/:wptId/*"
                            element={
                                <WaypointLanguage
                                    navigateToNext={(id: string, lang: string) =>
                                        goToWaypointScreen("waypoint-title", id, lang)
                                    }
                                />
                            }
                        />
                        <Route
                            path="waypoint-title/:wptId/:language"
                            element={
                                <WaypointTitle
                                    navigateToNext={(id: string, lang: string) =>
                                        goToWaypointScreen("waypoint-coordinates", id, lang)
                                    }
                                />
                            }
                        />
                        <Route
                            path="waypoint-coordinates/:wptId/:language"
                            element={
                                <WaypointCoordinates
                                    navigateToNext={(id: string, lang: string) =>
                                        goToWaypointScreen("waypoint-text", id, lang)
                                    }
                                />
                            }
                        />
                        <Route
                            path="waypoint-text/:wptId/:language"
                            element={
                                <WaypointText
                                    navigateToNext={(id: string, lang: string) =>
                                        goToWaypointScreen("waypoint-audio", id, lang)
                                    }
                                />
                            }
                        />
                        <Route
                            path="waypoint-audio/:wptId/:language"
                            element={
                                <WaypointAudio
                                    navigateToNext={(id: string, lang: string) =>
                                        goToWaypointScreen("waypoint-photos", id, lang)
                                    }
                                />
                            }
                        />
                        <Route
                            path="waypoint-info/:wptId/:language"
                            element={<WaypointInfo navigateToNext={() => goToStep("map-content", true)} />}
                        />
                        <Route
                            path="waypoint-photos/:wptId/:language"
                            element={<WaypointPicture navigateToNext={() => goToStep("map-content", true)} />}
                        />
                        <Route
                            path="/partners/partners-language/:hotinfoId/*"
                            element={
                                <HotInfoLanguage
                                    navigateToNext={(id: string, lang: string) =>
                                        goToHotInfoScreen("partners-coordinates", id, lang)
                                    }
                                />
                            }
                        />
                        <Route
                            path="/partners/partners-coordinates/:hotinfoId/:language"
                            element={
                                <HotInfoCoordinates
                                    navigateToNext={(id: string, lang: string) =>
                                        goToHotInfoScreen("partners-content", id, lang)
                                    }
                                />
                            }
                        />
                        <Route
                            path="/partners/partners-content/:hotinfoId/:language"
                            element={
                                <HotInfoContent
                                    navigateToNext={(id: string, lang: string) =>
                                        goToHotInfoScreen("partners-photos", id, lang)
                                    }
                                />
                            }
                        />
                        <Route
                            path="/partners/partners-photos/:hotinfoId/:language"
                            element={
                                <HotInfoPictures
                                    navigateToNext={(id: string, lang: string) => goToStep("partners", true)}
                                />
                            }
                        />
                        <Route
                            path="room-language/:roomId/*"
                            element={
                                <RoomLanguage
                                    navigateToNext={(id: string, lang: string) =>
                                        goToMapContentScreen("room-title", id, lang)
                                    }
                                />
                            }
                        />
                        <Route
                            path="room-title/:roomId/:language"
                            element={
                                <RoomTitle
                                    navigateToNext={(id: string, lang: string) =>
                                        goToWaypointScreen("room-plan", id, lang)
                                    }
                                />
                            }
                        />
                        <Route path="room-plan/:roomId/:language" element={<RoomPlan />} />
                        <Route
                            path="zone-language/:zoneId/*"
                            element={
                                <ZoneLanguage
                                    navigateToNext={(id: string, lang: string) =>
                                        goToMapContentScreen("zone-info", id, lang)
                                    }
                                />
                            }
                        />
                        <Route path="zone-info/:zoneId/:language" element={<ZoneInfo />} />
                        <Route
                            path="story-language/:planId/:storyId/*"
                            element={
                                <StoryLanguage
                                    navigateToNext={(id: string, lang: string, subplanId: string) =>
                                        goToMuseumStoriesScreen("story-title", subplanId, id, lang)
                                    }
                                />
                            }
                        />
                        <Route
                            path="story-title/:planId/:storyId/:language"
                            element={
                                <StoryTitle
                                    navigateToNext={(id: string, lang: string, subplanId: string) =>
                                        goToMuseumStoriesScreen("story-zone", subplanId, id, lang)
                                    }
                                />
                            }
                        />
                        <Route
                            path="story-zone/:planId/:storyId/:language"
                            element={
                                <StoryZone
                                    navigateToNext={(id: string, lang: string, subplanId: string) =>
                                        goToMuseumStoriesScreen("story-text", subplanId, id, lang)
                                    }
                                />
                            }
                        />
                        <Route
                            path="story-text/:planId/:storyId/:language"
                            element={
                                <StoryText
                                    navigateToNext={(id: string, lang: string, subplanId: string) =>
                                        goToMuseumStoriesScreen("story-audio", subplanId, id, lang)
                                    }
                                />
                            }
                        />
                        <Route
                            path="story-audio/:planId/:storyId/:language"
                            element={
                                <StoryAudio
                                    navigateToNext={(id: string, lang: string, subplanId: string) =>
                                        goToMuseumStoriesScreen("story-photos", subplanId, id, lang)
                                    }
                                />
                            }
                        />
                        <Route
                            path="story-photos/:planId/:storyId/:language"
                            element={
                                <StoryPhotos
                                    navigateToNext={(id: string, lang: string, subplanId: string) =>
                                        goToMuseumStoriesScreen("story-translate", subplanId, id, lang)
                                    }
                                />
                            }
                        />
                        {/* Image Scan Track Routes - ScanConfig */}
                        <Route
                            path="main-theme"
                            element={<MainTheme navigateToNext={() => goToStep("objects", true)} />}
                        />
                        <Route
                            path="objects"
                            element={<ManageObjects navigateToNext={() => goToStep("stories", true)} />}
                        />
                        <Route
                            path="objects/create"
                            element={<CreateObject navigateToNext={() => goToStep("objects")} />}
                        />
                        <Route
                            path="objects/modify/:objectId"
                            element={<ModifyObject navigateToNext={() => goToStep("objects")} />}
                        />
                        {/* Image Scan Track Routes - Stories */}
                        <Route
                            path="stories"
                            element={
                                <ManageStories
                                    navigateToNext={() => {
                                        if (trackData.hasProgressionSystem) {
                                            goToStep("avatar", true);
                                        } else {
                                            goToStep("incomplete-data", true);
                                        }
                                    }}
                                />
                            }
                        />
                        <Route
                            path="stories/create"
                            element={<CreateStory navigateToNext={() => goToStep("stories")} />}
                        />
                        <Route
                            path="stories/create/:objectId"
                            element={<CreateStory navigateToNext={() => goToStep("stories")} />}
                        />
                        <Route
                            path="stories/modify/:storyId"
                            element={<ModifyStory navigateToNext={() => goToStep("stories")} />}
                        />
                        {/* Content Playlist Routes */}
                        <Route
                            path="content-playlist"
                            element={<ManageContentPlaylist navigateToNext={() => goToStep("partners", true)} />}
                        />
                        <Route
                            path="content-playlist/create"
                            element={<CreateContentPlaylistItem navigateToNext={() => goToStep("content-playlist")} />}
                        />
                        <Route
                            path="content-playlist/modify/:storyId"
                            element={<ModifyContentPlaylistItem navigateToNext={() => goToStep("content-playlist")} />}
                        />
                        {/* Progression Routes */}
                        <Route
                            path="avatar"
                            element={<DefaultAvatar navigateToNext={() => goToStep("levels", true)} />}
                        />
                        <Route
                            path="levels"
                            element={<ManageLevels navigateToNext={() => goToStep("badges", true)} />}
                        />
                        <Route
                            path="levels/create"
                            element={<CreateLevel navigateToNext={() => goToStep("levels")} />}
                        />
                        <Route
                            path="levels/modify/:levelId"
                            element={<ModifyLevel navigateToNext={() => goToStep("levels")} />}
                        />
                        <Route
                            path="badges"
                            element={<ManageBadges navigateToNext={() => goToStep("rewards", true)} />}
                        />
                        <Route
                            path="badges/create"
                            element={<CreateBadge navigateToNext={() => goToStep("badges")} />}
                        />
                        <Route
                            path="badges/modify/:badgeId"
                            element={<ModifyBadge navigateToNext={() => goToStep("badges")} />}
                        />
                        <Route
                            path="rewards"
                            element={<ManageRewards navigateToNext={() => goToStep("social-share", true)} />}
                        />
                        <Route
                            path="rewards/create"
                            element={<CreateReward navigateToNext={() => goToStep("rewards")} />}
                        />
                        <Route
                            path="rewards/modify/:rewardId"
                            element={<ModifyReward navigateToNext={() => goToStep("rewards")} />}
                        />
                        <Route
                            path="social-share"
                            element={<SocialShare navigateToNext={() => goToStep("incomplete-data", true)} />}
                        />
                    </Routes>
                </div>
            </div>
        </div>
    );
};

export default TrackCreationFlow;
